/**
 * The largest photo Grow takes, in pixels on its longest side (D29).
 *
 * The server refuses a photo over this many pixels on a side with 413
 * PHOTO_TOO_LARGE_PIXELS (and `maxEdge` in the body, worded by
 * serverErrors.PHOTO_TOO_LARGE_PIXELS through lib/errorMessage, which stays as
 * the fallback). The app never lets it come to that: PhotoProcessor measures
 * the file that would be sent and shrinks one over the limit, so a gardener is
 * not told off for a big photo.
 *
 * The number is the Grow setting photos.MAX_EDGE_PX (lib/growSettings), the
 * server's own (config/s3 PHOTO_MAX_EDGE_PX); until the app has heard it there
 * is none, and the server's refusal stands.
 *
 * Who uses it: PhotoProcessor.
 *
 * @module photoLimit
 */

import { growSetting } from '@/shared/lib/growSettings';

/**
 * The longest side a photo may have, in pixels: the Grow setting, when it is
 * a positive number.
 *
 * @param saved - The setting's value, when the caller already has it (a component's useGrowSettings).
 * @returns The limit, in pixels, or undefined before the app has heard it.
 */
export function photoMaxEdge(saved: number | undefined = growSetting('photos.MAX_EDGE_PX')): number | undefined {
  return saved !== undefined && Number.isFinite(saved) && saved > 0 ? Math.round(saved) : undefined;
}

/**
 * The longest side of a picture file, in pixels, read by decoding it in the
 * browser.
 *
 * @param file - The picture as it would be sent.
 * @returns Its longest side, or null when the browser cannot decode it (the
 *   server then decides).
 */
export async function longestEdge(file: Blob): Promise<number | null> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file);
      const edge = Math.max(bitmap.width, bitmap.height);
      bitmap.close();
      return edge;
    } catch {
      // Fall through to an image element, which some browsers decode more of.
    }
  }
  if (typeof document === 'undefined') return null;
  const url = URL.createObjectURL(file);
  try {
    const image = document.createElement('img');
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Could not decode the picture'));
      image.src = url;
    });
    return Math.max(image.naturalWidth, image.naturalHeight) || null;
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}
